import { useRef } from 'react'
import { BellIcon, QuestionIcon, WarningIcon } from '@chakra-ui/icons'
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogOverlay,
    Box,
    Button,
    HStack,
    Icon,
    Text,
    VStack,
} from '@chakra-ui/react'
import { formatDuration, intervalToDuration } from 'date-fns'

type EarlySaveConfirmationDialogProps = {
    isOpen: boolean
    onClose: () => void
    onSaveAsPossibleLineup: () => void
    onSaveAsLineupAnyway: () => void
    matchDate: Date | null
    isLoading?: boolean
}

export function EarlySaveConfirmationDialog({
    isOpen,
    onClose,
    onSaveAsPossibleLineup,
    onSaveAsLineupAnyway,
    matchDate,
    isLoading = false,
}: EarlySaveConfirmationDialogProps) {
    const cancelRef = useRef<HTMLButtonElement>(null)

    const timeUntilMatch = matchDate
        ? formatDuration(intervalToDuration({ start: new Date(), end: matchDate }), {
              format: ['days', 'hours', 'minutes'],
              zero: true,
              delimiter: ' ',
          })
        : ''

    return (
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
            <AlertDialogOverlay>
                <AlertDialogContent maxW="md">
                    <AlertDialogBody py={6}>
                        <VStack spacing={5}>
                            <Box
                                bg="orange.100"
                                borderRadius="full"
                                p={3}
                                display="flex"
                                alignItems="center"
                                justifyContent="center">
                                <Icon as={WarningIcon} boxSize={6} color="orange.500" />
                            </Box>

                            <Text fontSize="xl" fontWeight="bold" textAlign="center">
                                Save lineup this early?
                            </Text>

                            <Text fontSize="md" color="gray.600" textAlign="center">
                                The match starts in{' '}
                                <Text as="span" fontWeight="bold">
                                    {timeUntilMatch}
                                </Text>
                                .
                            </Text>

                            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={4} w="100%">
                                <HStack align="flex-start" spacing={3}>
                                    <Icon as={BellIcon} boxSize={5} color="red.500" mt={0.5} />
                                    <Box>
                                        <Text fontWeight="semibold" color="red.600" mb={1}>
                                            Push notification warning
                                        </Text>
                                        <Text fontSize="sm" color="red.700">
                                            Saving as confirmed lineup will trigger a push notification to all
                                            subscribed users.
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>

                            <Box
                                bg="green.50"
                                border="1px solid"
                                borderColor="green.200"
                                borderRadius="md"
                                p={4}
                                w="100%">
                                <HStack align="flex-start" spacing={3}>
                                    <Icon as={QuestionIcon} boxSize={5} color="green.500" mt={0.5} />
                                    <Box>
                                        <Text fontWeight="semibold" color="green.600" mb={1}>
                                            Suggestion
                                        </Text>
                                        <Text fontSize="sm" color="green.700">
                                            Consider saving as{' '}
                                            <Text as="span" fontWeight="bold">
                                                possible lineup
                                            </Text>{' '}
                                            instead. You can confirm it closer to kick-off without sending early
                                            notifications.
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>

                            <VStack spacing={3} w="100%" pt={2}>
                                <Button
                                    colorScheme="green"
                                    w="100%"
                                    onClick={onSaveAsPossibleLineup}
                                    leftIcon={<QuestionIcon />}
                                    isLoading={isLoading}>
                                    Save as Possible Lineup
                                </Button>
                                <Button
                                    variant="outline"
                                    colorScheme="orange"
                                    w="100%"
                                    onClick={onSaveAsLineupAnyway}
                                    leftIcon={<BellIcon />}
                                    isLoading={isLoading}>
                                    Save as Lineup Anyway
                                </Button>
                                <Button ref={cancelRef} variant="ghost" w="100%" onClick={onClose}>
                                    Cancel
                                </Button>
                            </VStack>
                        </VStack>
                    </AlertDialogBody>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    )
}
